import {
  Avatar,
  Button,
  Card,
  Divider,
  Dropdown,
  Flex,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import {
  CalendarDays,
  Ban,
  ChevronDown,
  Clock,
  Key,
  Mail,
  MoreHorizontal,
  Pencil,
  Phone,
  ShieldCheck,
  Smartphone,
  StopCircle,
  Trash2,
  Undo2,
  UserCog,
  UserRound
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { OrganizationRole } from 'src/api/main'
import { DateTimeCell, computeInitials, pickAvatarColor } from 'src/components'
import { formatPhoneNumber } from 'src/utils'

import type { UserStatus, WorkspaceUser } from '../types'
import {
  composeDisplayName,
  deriveInviteState,
  deriveMfaActions
} from '../utils'

import { UsersRoleTag } from './UsersRoleTag'
import { UsersStatusPill } from './UsersStatusPill'

const { Paragraph, Text, Title } = Typography

export type UserHeroAction =
  | 'edit'
  | 'resetPassword'
  | 'resendInvite'
  | 'revokeInvite'
  | 'reactivate'
  | 'suspend'
  | 'delete'
  | 'requireMfa'
  | 'disableMfa'
  | 'resetMfa'
  | 'impersonate'

type Props = {
  record: WorkspaceUser
  status: UserStatus
  onAction: (action: UserHeroAction) => void
  /** Impersonation is owner-only — gate the "log in as" button. */
  canImpersonate?: boolean
  /** `users.manage` — edit, MFA, suspend, delete, password reset. */
  canManage?: boolean
  /** `users.invite` — resend and revoke a pending invite. */
  canInvite?: boolean
}

const metaStyle = {
  color: 'var(--ant-color-text-tertiary)',
  fontSize: 12
} as const

export const UserDetailHero = ({
  record,
  status,
  onAction,
  canImpersonate = false,
  canManage = false,
  canInvite = false
}: Props) => {
  const dispatch = (action: UserHeroAction) => () => onAction(action)
  const { t } = useTranslation()
  const name = composeDisplayName(
    record.user.first_name,
    record.user.last_name,
    record.user.email
  )
  const lastLogin = record.user.last_login
  const formattedPhone = formatPhoneNumber(record.user.phone)
  const sourceLabel = record.user.sso_id
    ? t('users.hero.source.sso')
    : t('users.hero.source.manual')
  const managerName = record.head_user
    ? composeDisplayName(
        record.head_user.first_name,
        record.head_user.last_name,
        record.head_user.email
      )
    : null

  const inviteState = deriveInviteState(record)
  const accepted = inviteState === 'accepted'
  const mfaActions = deriveMfaActions(record, accepted)
  const inviteExpiresAt = record.invite_expires_at

  // Heading row: status / role / MFA / SSO, joined by vertical dividers.
  const headingMetaItems: ReactNode[] = [
    <Tooltip key="status" title={t('users.hero.statusHint')}>
      <span>
        <UsersStatusPill status={status} />
      </span>
    </Tooltip>,
    <Tooltip key="role" title={t('users.hero.roleHint')}>
      <span>
        <UsersRoleTag role={record.role as OrganizationRole} />
      </span>
    </Tooltip>,
    <Tooltip key="mfa" title={t('users.notApiBacked.mfa')}>
      <Tag
        icon={<ShieldCheck size={11} style={{ marginInlineEnd: 4 }} />}
        color="warning"
        style={{
          alignItems: 'center',
          display: 'inline-flex',
          fontSize: 11,
          lineHeight: 1.35,
          marginInlineEnd: 0
        }}
      >
        {t('users.hero.mfaUnknown')}
      </Tag>
    </Tooltip>,
    <Tooltip key="via" title={t('users.hero.ssoHint')}>
      <Flex align="center" gap={4}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {t('users.hero.via')}
        </Text>
        <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>{sourceLabel}</Tag>
      </Flex>
    </Tooltip>
  ]

  // Meta row: email · phone · reports-to · joined · last sign-in, dot-joined.
  const topMetaItems: ReactNode[] = [
    <Tooltip key="user-id" title={t('users.hero.userId')}>
      <Text
        type="secondary"
        style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
      >
        {`# ${record.user.id}`}
      </Text>
    </Tooltip>,
    <Flex key="email" align="center" gap={5}>
      <Mail size={12} />
      <Text
        copyable={{ tooltips: false }}
        style={{ fontSize: 12 }}
        ellipsis={{ tooltip: record.user.email }}
      >
        {record.user.email}
      </Text>
    </Flex>,
    formattedPhone ? (
      <Flex key="phone" align="center" gap={5}>
        <Phone size={12} />
        <Text
          style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          type="secondary"
        >
          {formattedPhone}
        </Text>
      </Flex>
    ) : null,
    managerName ? (
      <Flex key="manager" align="center" gap={5}>
        <UserRound size={12} />
        <Text style={{ fontSize: 12 }} type="secondary">
          {t('users.detail.workspaceAccess.headUser')}: {managerName}
        </Text>
      </Flex>
    ) : null,
    record.joined_date ? (
      <Flex key="joined" align="center" gap={5}>
        <CalendarDays size={12} />
        <Text style={{ fontSize: 12 }} type="secondary">
          {t('users.detail.workspaceAccess.joined')}:{' '}
        </Text>
        <DateTimeCell
          value={record.joined_date}
          variant="date"
          style={{ fontSize: 12 }}
        />
      </Flex>
    ) : null,
    <Flex key="last-login" align="center" gap={5}>
      <Clock size={12} />
      <Text style={{ fontSize: 12 }} type="secondary">
        {t('users.hero.lastSignIn')}:{' '}
      </Text>
      <DateTimeCell
        value={lastLogin}
        variant="relative"
        empty={t('users.lastSignIn.never')}
        style={{ fontSize: 12 }}
      />
    </Flex>
  ].filter(Boolean) as ReactNode[]

  const renderDividedRow = (items: ReactNode[]) =>
    items.flatMap((item, index) =>
      index < items.length - 1
        ? [
            item,
            <Divider
              key={`divider-${index}`}
              type="vertical"
              style={{ margin: 0 }}
            />
          ]
        : [item]
    )

  const renderDottedRow = (items: ReactNode[]) =>
    items.flatMap((item, index) =>
      index < items.length - 1
        ? [
            item,
            <Text
              key={`dot-${index}`}
              type="secondary"
              style={{ fontSize: 12 }}
            >
              ·
            </Text>
          ]
        : [item]
    )

  return (
    <Card styles={{ body: { padding: 0 } }}>
      <Flex
        justify="space-between"
        align="flex-start"
        gap={16}
        style={{ padding: 12 }}
      >
        <Flex gap={14} style={{ minWidth: 0 }}>
          <Avatar
            size={48}
            src={record.user.photo?.file ?? undefined}
            style={{
              background: record.user.photo?.file
                ? 'transparent'
                : pickAvatarColor(name || record.user.email),
              color: '#fff',
              fontSize: 18,
              fontWeight: 600,
              flex: '0 0 auto'
            }}
          >
            {computeInitials(
              record.user.first_name,
              record.user.last_name,
              record.user.email
            )}
          </Avatar>
          <Flex vertical gap={6} style={{ minWidth: 0 }}>
            <Flex align="center" gap={8} wrap>
              <Title
                level={3}
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 600,
                  lineHeight: 1.2
                }}
              >
                {name}
              </Title>
              {renderDividedRow(headingMetaItems)}
            </Flex>
            <Flex align="center" gap={8} wrap style={metaStyle}>
              {renderDottedRow(topMetaItems)}
            </Flex>
          </Flex>
        </Flex>

        <Flex gap={6} align="center" wrap>
          {canInvite && inviteState && inviteState !== 'accepted' ? (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'resend',
                    icon: <Mail size={14} />,
                    label: t('users.actions.resendInvite'),
                    onClick: dispatch('resendInvite')
                  },
                  ...(inviteState !== 'revoked'
                    ? [
                        {
                          key: 'revoke',
                          icon: <Ban size={14} />,
                          label: t('users.lifecycle.revokeInvite.action'),
                          danger: true,
                          onClick: dispatch('revokeInvite')
                        }
                      ]
                    : [])
                ]
              }}
            >
              <Button type="primary" size="small" icon={<Mail size={14} />}>
                <Flex align="center" gap={4}>
                  {t('users.actions.invitation')}
                  <ChevronDown size={14} />
                </Flex>
              </Button>
            </Dropdown>
          ) : null}
          {status === 'suspended' && accepted ? (
            <Button
              type="primary"
              size="small"
              icon={<Undo2 size={14} />}
              onClick={dispatch('reactivate')}
            >
              {t('users.actions.reactivate')}
            </Button>
          ) : null}
          {canImpersonate && status === 'active' && accepted ? (
            <Button
              size="small"
              icon={<UserCog size={14} />}
              onClick={dispatch('impersonate')}
            >
              {t('users.actions.impersonate')}
            </Button>
          ) : null}
          {canManage && accepted ? (
            <Button
              size="small"
              icon={<Key size={14} />}
              onClick={dispatch('resetPassword')}
            >
              {t('users.actions.resetPassword')}
            </Button>
          ) : null}
          {canManage ? (
            <Button
              type="primary"
              size="small"
              icon={<Pencil size={14} />}
              onClick={dispatch('edit')}
            >
              {t('users.actions.edit')}
            </Button>
          ) : null}
          {canManage ? (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  // MFA actions — admin-managed only (hidden for non-admin viewers,
                  // whose flags arrive null). The require/disable toggle keys off
                  // the workspace requirement (mfa_required) those actions change,
                  // NOT the member's own enrollment; reset is account recovery and
                  // only applies once the member has actually enrolled a factor.
                  ...(mfaActions.requireMfa
                    ? [
                        {
                          key: 'mfa-require',
                          icon: <Smartphone size={14} />,
                          label: t('users.actions.requireMfa'),
                          onClick: dispatch('requireMfa')
                        }
                      ]
                    : []),
                  ...(mfaActions.disableMfa
                    ? [
                        {
                          key: 'mfa-disable',
                          icon: <Smartphone size={14} />,
                          label: t('users.actions.disableMfa'),
                          danger: true,
                          onClick: dispatch('disableMfa')
                        }
                      ]
                    : []),
                  ...(mfaActions.resetMfa
                    ? [
                        {
                          key: 'mfa-reset',
                          icon: <Smartphone size={14} />,
                          label: t('users.actions.resetMfa'),
                          onClick: dispatch('resetMfa')
                        }
                      ]
                    : []),
                  { type: 'divider' as const },
                  // Suspend / reactivate flip the member's `blocked` flag, which
                  // the backend only allows once the invite is accepted.
                  ...(accepted
                    ? [
                        status === 'suspended'
                          ? {
                              key: 'restore',
                              icon: <Undo2 size={14} />,
                              label: t('users.actions.reactivate'),
                              onClick: dispatch('reactivate')
                            }
                          : {
                              key: 'suspend',
                              icon: <StopCircle size={14} />,
                              label: t('users.actions.suspend'),
                              onClick: dispatch('suspend')
                            }
                      ]
                    : []),
                  {
                    key: 'delete',
                    icon: <Trash2 size={14} />,
                    label: t('users.actions.delete'),
                    danger: true,
                    onClick: dispatch('delete')
                  }
                ]
              }}
            >
              <Button
                size="small"
                icon={<MoreHorizontal size={14} />}
                aria-label={t('users.actions.more')}
              />
            </Dropdown>
          ) : null}
        </Flex>
      </Flex>

      {record.user.last_login == null ? (
        <div
          style={{
            padding: '10px 20px',
            background: 'var(--ant-color-warning-bg)',
            borderTop: '1px solid var(--ant-color-warning-border)',
            color: 'var(--ant-color-warning-text)',
            fontSize: 12
          }}
        >
          <Paragraph style={{ margin: 0, fontSize: 12 }}>
            {t('users.hero.alerts.neverSignedIn')}
            {inviteExpiresAt && inviteState !== 'revoked' ? (
              <span style={{ marginLeft: 6 }}>
                ·{' '}
                {inviteState === 'expired'
                  ? t('users.lifecycle.inviteExpired', { when: '' })
                  : t('users.lifecycle.inviteExpires', { when: '' })}
                <DateTimeCell
                  value={inviteExpiresAt}
                  variant="relative"
                  style={{ fontSize: 12 }}
                />
              </span>
            ) : null}
          </Paragraph>
        </div>
      ) : null}
    </Card>
  )
}
